/*  Function to format instruction commands from a block of text
    We want to detect patterns like (OOC: This is some text)
    - Usually the instruction is on one line and has the keyword OOC:
    - It can be inserted in the middle of the text
    - It can be enclosed in (), (()), **, or even *()*
    - It can be used without being enclosed
    - It can be at the beginning of the text
    - It can be at the end of the text
    - It can be at the beginning of a line
    - It can be at the end of a line
    - It can happen that it begins with a ( and its not properly closed
    - It can happen that it ends with a ) and its not properly opened
    - the keyword OOC can be in uppercase or lowercase

    For the /CMD syntax:
    - The instruction always starts with /CMD at beginning of line
    - There can be a colon after the /CMD, with optional space
    - There can be content on lines before the command
    - The command can be on a line by itself
    - The command can be followed by content on a new line
    - The cmd can be case insensitive

    For the /SEARCH syntax:
    - Similar to /CMD but specifically for search queries
    - The instruction always starts with /SEARCH at beginning of line
    - There can be a colon after the /SEARCH, with optional space
    - The search can be case insensitive
    */

export enum CommandType {
  OOC = "ooc",
  CMD = "cmd",
  SEARCH = "search",
}

export interface FormatResult {
  content: string;
  cleanContent: string;
  commandsContent: string;
  hasCommands: boolean;
}

// Helper function to escape backticks
const escapeBackticks = (text: string): string => {
  return text.replace(/`/g, "\\`");
};

export const formatInstructionCommandsV2 = (content: string): FormatResult => {
  const originalContent = content;
  let modifiedContent = content;

  // Regular expressions to match various instruction patterns (with global flag for multiple matches)
  const oocRegex = /([(*[\])]*\b[oO][oO][cC]:\s*([^()\n[\]*]*)([(*[\])]*))/gi;
  const cmdRegex = /^\s*\/[cC][mM][dD]:?\s*(.*)$/gim;
  const searchRegex = /^\s*\/[sS][eE][aA][rR][cC][hH]:?\s*(.*)$/gim;

  let hasAnyCommands = false;

  // Process all /SEARCH patterns first
  let matches = [...content.matchAll(searchRegex)];
  for (const match of matches) {
    if (match[1]) {
      const instruction = escapeBackticks(match[1].trim());
      const formattedInstruction = `<aside role="${CommandType.SEARCH}">${instruction}</aside>`;
      modifiedContent = modifiedContent.replace(match[0], formattedInstruction);
      hasAnyCommands = true;
    }
  }

  // Process all /CMD patterns
  matches = [...modifiedContent.matchAll(cmdRegex)];
  for (const match of matches) {
    if (match[1]) {
      const instruction = escapeBackticks(match[1].trim());
      const formattedInstruction = `<aside role="${CommandType.CMD}">${instruction}</aside>`;
      modifiedContent = modifiedContent.replace(match[0], formattedInstruction);
      hasAnyCommands = true;
    }
  }

  // Process all OOC patterns
  matches = [...modifiedContent.matchAll(oocRegex)];
  for (const match of matches) {
    if (match[2]) {
      const instruction = escapeBackticks(match[2].trim());
      const formattedInstruction = `<aside role="${CommandType.OOC}">${instruction}</aside>`;
      modifiedContent = modifiedContent.replace(match[0], formattedInstruction);
      hasAnyCommands = true;
    }
  }

  // Clean up extra spaces (but preserve newlines)
  modifiedContent = modifiedContent
    .replace(/[ \t]+/g, " ")
    .replace(/[ \t]*\n[ \t]*/g, "\n")
    .trim();

  // Separate clean content from commands
  let cleanContent = originalContent;
  let commandsContent = "";
  const hasCommands = hasAnyCommands;

  if (hasCommands) {
    // Remove command patterns from original content to get clean content
    cleanContent = originalContent
      .replace(/^\s*\/[sS][eE][aA][rR][cC][hH]:?\s*.*$/gm, "")
      .replace(/^\s*\/[cC][mM][dD]:?\s*.*$/gm, "")
      .replace(/([(*[\])]*\b[oO][oO][cC]:\s*([^()\n[\]*]*)([(*[\])]*))/gi, "")
      .replace(/[ \t]+/g, " ")
      .replace(/[ \t]*\n[ \t]*/g, "\n")
      .trim();

    // Extract commands from modified content
    const asideMatches = modifiedContent.match(/<aside role="(search|cmd|ooc)">.*?<\/aside>/g);
    if (asideMatches) {
      commandsContent = asideMatches.join("");
    }
  }

  return {
    content: modifiedContent,
    cleanContent,
    commandsContent,
    hasCommands,
  };
};
